import {
  AfterViewInit,
  Component, ElementRef, NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import {
  ActionEventArgs,
  AgendaService,
  DayService,
  DragAndDropService,
  EventSettingsModel,
  ExcelExportService,
  GroupModel,
  ICalendarExportService,
  ICalendarImportService,
  MonthAgendaService,
  MonthService,
  ResizeService,
  ResourceDetails,
  ScheduleComponent,
  TimelineMonthService,
  TimelineViewsService,
  TimelineYearService,
  ToolbarActionArgs,
  WeekService,
  WorkWeekService,
  YearService
} from "@syncfusion/ej2-angular-schedule";
import {
  AccordionAnimationSettingsModel,
  AccordionComponent,
  ClickEventArgs,
  ExpandMode,
  ItemModel, TabAnimationSettingsModel,
  TabComponent,
  ToolbarComponent
} from "@syncfusion/ej2-angular-navigations";
import {ScheduleModel} from "../../core/services/schedule-data/schedule.model";
import {KnockoutFieldModel} from "../../core/components/knockout-field/knockout-field.model";
import {addClass, removeClass} from "@syncfusion/ej2-base";
import {Subscription} from "rxjs";
import {ScheduleConfigurationModel} from "../../core/services/schedule-data/schedule-configuration.model";
import {ActivatedRoute} from "@angular/router";
import {UtilService} from "../../core/services/util/util.service";
import {ScheduleDataService} from "../../core/services/schedule-data/schedule-data.service";
import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";

import {cloneDeep} from 'lodash';
import {ChangeEventArgs as SwitchEventArgs} from "@syncfusion/ej2-buttons/src/common/common";
import {CheckBoxSelectionService} from "@syncfusion/ej2-angular-dropdowns";
import {ScheduleEventModel} from "../../core/services/schedule-data/schedule-event.model";
import {RequestService} from "../../core/services/request/request.service";
import { ResizeObserver } from '@juggle/resize-observer';
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";
import {UserService} from "../../core/services/user/user.service";

@Component({
  selector: 'sb-forward-schedule-of-change-page',
  templateUrl: './forward-schedule-of-change-page.component.html',
  styleUrls: ['./forward-schedule-of-change-page.component.scss'],
  encapsulation: ViewEncapsulation.None, // Needed to allow us to override global boostrap4 styles
  providers: [
    DayService,
    WeekService,
    WorkWeekService,
    MonthService,
    YearService,
    AgendaService,
    MonthAgendaService,
    TimelineViewsService,
    TimelineMonthService,
    TimelineYearService,
    CheckBoxSelectionService,
    DragAndDropService,
    ResizeService,
    ICalendarExportService,
    ICalendarImportService,
    ExcelExportService
  ],
})
export class ForwardScheduleOfChangePageComponent implements OnInit, AfterViewInit, OnDestroy {

  QUERY_CHANGE = "Change";
  QUERY_MAINTENANCE = "MaintenanceSchedule";
  QUERY_BLACKOUT = "BlackoutSchedule";
  QUERY_CMDB = "CMDB";
  QUERY_EMPTY = '{"and":[]}';

  @ViewChild('schedule') schedule: ScheduleComponent;

  @ViewChild('filterAccordion') public filterAccordion: AccordionComponent;

  @ViewChild('filterTabs') public filterTabs: TabComponent;

  @ViewChild('scheduleToolbar') public scheduleToolbar: ToolbarComponent;

  @ViewChild('leftPanel') public leftPanel: ElementRef;

  @ViewChild('rightPanel') public rightPanel: ElementRef;

  // Component data

  public title = ''; // name of ScheduleConfiguration

  public subtitle = ''; // description of ScheduleConfiguration

  // The 'toggleSchedule' flag is there because Syncfusion Schedule really wants to have much of its
  // configuration defined up front.
  // If we want to change things like views, resources, groups, etc we have to do it
  // in one tick and then toggle the toggleSchedule flag to tear down a copy of Schedule
  // and build a new one. Note that for things that are commonly changed, such as the
  // current view or the current date, the same problem does not arise - it appears to only be
  // reference data related.
  public toggleSchedule = false;

  // The 'configurationReady' flag is to ensure that no attempt is made to build Schedule until
  // we have received our configuration data from the server.
  public configurationReady = false;

  // Should we accept live updates from the server
  public liveUpdate = true;

  // Should we obey grouping info in the configuration?
  public grouping = true;

  // Should we merge all resource configuration into a single grouping tree?
  public mergeGrouping = true;

  // Component data for 'schedule'

  // The current synthetic ScheduleModel built from the current configuration
  public scheduleModel: ScheduleModel = {};

  public eventSettings: EventSettingsModel = {};

  public group: GroupModel = {};

  public emptyGroup: GroupModel = {};

  public timezone: string = 'UTC';

  // Component data for 'filterAccordion'

  public expandMode: ExpandMode = "Multiple";

  public filterAccordionAnimation: AccordionAnimationSettingsModel = {
    collapse: {
      effect: 'SlideUp',
      duration: 100,
      easing: 'linear'
    },
    expand: {
      effect: 'SlideDown',
      duration: 100,
      easing: 'linear'
    }
  };

  public tabAnimation: TabAnimationSettingsModel = {
    previous: {
      effect: 'None'
    },
    next: {
      effect: 'None'
    }
  };

  // Filter support

  public queryFilterConfig: { [key: string]: KnockoutFieldModel } = {};

  // Private data

  private queryFilterCurrentQuery: { [key: string]: string }= {
  };

  // The query builder will call the value change callback a couple of times as it is being
  // initialised - we need to ignore them until it has been stabilised.
  private queryFilterIgnoreValueChange: { [key: string]: boolean }= {
    Change: true,
    CMDB: true
  };
  private componentFirstDisplay = true;

  private activatedRouteSubscription: Subscription | null;

  private scheduleContentChangedSubscription: Subscription;

  // Id of the current schedule configuration
  private scheduleId: string;

  // Key of the current schedule configuration
  private scheduleKey: string;

  // Optional aspect coming from the path
  private urlAspect: string;

  // The current schedule configuration from the server
  public scheduleConfiguration: ScheduleConfigurationModel;

  // The current set of raw records loaded from the event datasources
  private records: any[] = [];

  // Resize support

  @ViewChild("fsocWrapper", {static: false})
  private fsocWrapper: ElementRef;

  private resizeObserver: ResizeObserver;

  constructor(private activatedRoute: ActivatedRoute,
              private utilService: UtilService,
              private requestService: RequestService,
              private scheduleDataService: ScheduleDataService,
              private navigateService: LegacyNavigateService,
              private i18nService: InternationalizationService,
              private userService: UserService,
              private zone: NgZone) { }

  async ngOnInit() {
    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    // Mark as angular page
    document.getElementById("detailMain")?.classList?.add("sb-schedule-page-content");

    // Identify the displayType from the route.
    this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {
      this.scheduleId = params.get("id"); // 'id' of ScheduleConfiguration or a key, to be determined at loadConfiguration
      this.urlAspect = params.get("aspect") || null; // Optional aspect

      const allQueryParams = (this.activatedRoute.queryParams as any).value;
      Object.keys(allQueryParams).forEach((param) => {
          const paramValue = allQueryParams[param];
          const suffix = "Query";
          if (param.endsWith(suffix)) {
            const kind = param.substring(0, param.length - suffix.length);
            this.queryFilterCurrentQuery[kind] = paramValue;
          } else if (param === "query") {
            // query by itself is assumed to mean ChangeQuery
            this.queryFilterCurrentQuery[this.QUERY_CHANGE] = paramValue;
          }
      });

      // We may be changing routes on the same Schedule component so we need to clear it all first.
      if (!this.componentFirstDisplay) {
        // TODO some kind of clean up activity might be needed, such as clearing state.
      }

      // We may be getting filters coming in the URL
      this.initialiseFilters();
    });

    // Subscribe to service receiving schedule changes from the server
    this.scheduleContentChangedSubscription = this.scheduleDataService.scheduleContentChanged.subscribe(async (_) => {
      if (this.liveUpdate && this.scheduleModel.liveUpdate) {
        if (!this.componentFirstDisplay) {
          this.onRefreshData(null);
        }
      }
    });

    await this.loadConfiguration();

    if (this.scheduleConfiguration != null) {
      await this.loadRecords();
      await this.applyConfiguration();
      this.applyRecords();
    } else {
      window["DialogAPI"].message("Error", "Error: Could not find schedule [" + this.scheduleId + "]" );
    }
    this.configurationReady = true;

    // Resize support
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      this.zone.run(() => {
        entries.forEach((e) => {
          if (e.target === document.body) {
            if (this.schedule && this.fsocWrapper) {
              const requiredHeight = (e.contentRect.height - this.fsocWrapper.nativeElement.getBoundingClientRect().top - 120) + "px";
              this.leftPanel.nativeElement.style.height = requiredHeight;
              this.rightPanel.nativeElement.style.height = requiredHeight;
            }
          }
        });
      });
    });
    this.resizeObserver.observe(document.body);

  }

  ngAfterViewInit() {
    this.componentFirstDisplay = false;
  }

  ngOnDestroy() {
    this.scheduleContentChangedSubscription && this.scheduleContentChangedSubscription.unsubscribe();
  }

  private initialiseFilters() {

      this.queryFilterConfig[this.QUERY_CHANGE] = {
        name: 'String',
        datatype: 'string',
        availableValues: [],
        referenceTable: null,
        readonly: false,
        mandatory: false,
        enablePreview: false,
        tooltip: null,
        data: {
          defaultValue: null, // null | {value?: string, displayValue?: string };
          defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
          answer: {
            value: this.queryFilterCurrentQuery[this.QUERY_CHANGE],
            displayValue: this.queryFilterCurrentQuery[this.QUERY_CHANGE]
          }, // null | {value?: string, displayValue?: string };
        },
        changed: null,
        requestId: null,
        table: 'SyntheticTable',
        renderer: 'condition',
        rendererProperties: {
          "dependent.table": "Change",
          onApplyFilter:  (value) => this.queryOnApplyFilter(value, this.QUERY_CHANGE),
          "update.model.on.change": true,
          showFooter: true,
          showApply:true,
          showCancel:false,
        },
        valueChangeCallback: (value) => this.queryValueChangeCallback(value, this.QUERY_CHANGE),
      };

      this.queryFilterConfig[this.QUERY_CMDB] = {
        name: 'String',
        datatype: 'string',
        availableValues: [],
        referenceTable: null,
        readonly: false,
        mandatory: false,
        enablePreview: false,
        tooltip: null,
        data: {
          defaultValue: null, // null | {value?: string, displayValue?: string };
          defaultValueSource: 'script', // 'nlu' | 'function' | 'question' | 'script'; // Note: script also implies it is a constant
          answer: {
            value: this.queryFilterCurrentQuery[this.QUERY_CMDB],
            displayValue: this.queryFilterCurrentQuery[this.QUERY_CMDB]
          }, // null | {value?: string, displayValue?: string };
        },
        changed: null,
        requestId: null,
        table: 'SyntheticTable',
        renderer: 'condition',
        rendererProperties: {
          "dependent.table": "CMDB",
          onApplyFilter:  (value) => this.queryOnApplyFilter(value, this.QUERY_CMDB),
          "update.model.on.change": true,
          showFooter: true,
          showApply:true,
          showCancel:false,
        },
        valueChangeCallback:  (value) => this.queryValueChangeCallback(value, this.QUERY_CMDB),
      };
  }

  // Fake button navigation

  public buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  public canGoBack() {
    return this.navigateService.canGoBack();
  }

  public back() {
    this.navigateService.back();
  }

  // Event handlers

  public onScheduleCreated(arg: any) {
    // Some configuration is set as inputs to the angular component in the template, while others are set here. The ones set
    // here are conditional because the schedule component changes behaviour depending on whether they are set or not,
    // even if you set it to the default value or undefined or null, and you don't get a choice when they are done in the template.

    if (this.scheduleModel.selectedDate) {
      this.schedule.selectedDate = this.scheduleModel.selectedDate;
    }
    if (this.scheduleModel.minDate) {
      this.schedule.minDate = this.scheduleModel.minDate;
    }
    if (this.scheduleModel.maxDate) {
      this.schedule.maxDate = this.scheduleModel.maxDate;
    }
    if (this.scheduleModel.dateFormat) {
      this.schedule.dateFormat = this.scheduleModel.dateFormat;
    }

    // TODO: might need to be in dataBound
    this.queryFilterIgnoreValueChange[this.QUERY_CHANGE] = false;
    this.queryFilterIgnoreValueChange[this.QUERY_CMDB] = false;
  }

  public onActionBegin(args: ActionEventArgs & ToolbarActionArgs) {
    if (args.requestType === 'toolbarItemRendering') {

      // const exportExcelItem: ItemModel = {
      //   align: 'Right', showTextOn: 'Both', prefixIcon: 'e-icon-schedule-excel-export',
      //   text: 'Excel Export', cssClass: 'e-excel-export', click: this.onExportExcelClick.bind(this)
      // };
      // const exportICalItem: ItemModel = {
      //   align: 'Right', showTextOn: 'Both', prefixIcon: 'e-icon-schedule-excel-export',
      //   text: 'ICal Export', cssClass: 'e-excel-export', click: this.onExportICalClick.bind(this)
      // };

      const timelineSeparator: ItemModel = {
        align: 'Right', type: 'Separator'
      };
      const separator: ItemModel = {
        align: 'Right', type: 'Separator'
      };
      const createChange: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-plus',
        tooltipText: 'Create change',
        click: this.onCreateChange.bind(this)
      };
      const refreshData: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-refresh',
        tooltipText: 'Refresh data',
        click: this.onRefreshData.bind(this)
      };
      const refreshAll: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-refresh sb-fsoc-refresh-all-colour',
        tooltipText: 'Refresh all',
        click: this.onRefreshAll.bind(this)
      };
      const toggleSettings: ItemModel = {
        align: 'Right', showTextOn: 'Both', prefixIcon: 'e-settings',
        tooltipText: 'Settings',
        click: this.onSettingsClick.bind(this)
      };

      args.items.splice(args.items.findIndex((item) => item.text === 'Month') + 1, 0, timelineSeparator);

      // args.items.push(exportExcelItem);
      // args.items.push(exportICalItem);
      args.items.push(separator);
      args.items.push(createChange);
      args.items.push(refreshData);
      args.items.push(refreshAll);
      args.items.push(toggleSettings);
    }
  }

  public async onCreateChange(args: ClickEventArgs) {
  }

  public async onRefreshData(args: ClickEventArgs) {
    await this.refreshResources();
    await this.loadRecords();
    this.applyRecords();
  }

  public async onRefreshAll(args: ClickEventArgs) {
    await this.loadConfiguration();
    await this.loadRecords();
    await this.applyConfiguration();
    this.applyRecords();
    this.toggleScheduleComponent();
  }

  public onGroupingChange(args: SwitchEventArgs): void {
    this.grouping = args.checked;
    this.onRefreshAll(null);
  }

  public onLiveUpdateChange(args: SwitchEventArgs): void {
    this.liveUpdate = args.checked;
  }

  public onDataBound(args: any) {
  }

  public onEventRendered(args: any) {
  }

  public onActionComplete(args: any) {
  }

  public onActionFailure(args: any) {
  }

  public onSettingsClick(args): void {
    const settingsPanel: Element = document.querySelector('.sb-fsoc-content .right-panel');
    if (settingsPanel.classList.contains('hide')) {
      removeClass([settingsPanel], 'hide');
    } else {
      addClass([settingsPanel], 'hide');
    }
    this.schedule.refreshEvents();
  }

  // Preview support - very similar to table-grid, might want to make a component from it

  clickPreview(e: MouseEvent, data: ResourceDetails | Record<string, any>) {
    // Depending on where the preview is being invoked we might have different data coming in.
    // ResourceDetails is from the grouping tree in timeline views and Record<string,any> is from the
    // quick info popup.
    const resourceData = (data as ResourceDetails).resourceData || data;
    return this.clickPreviewImpl(e, resourceData);
  }

  // TODO: This mucking about with state on the element should be done with an Angular directive.
  clickPreviewImpl(e: MouseEvent, data: Record<string, any>) {

    const initClicks = (e: MouseEvent) => {
      if ((e.target as any)._clicks === null || (e.target as any)._clicks === undefined) {
        (e.target as any)._clicks = 0;
      }
    };
    const getClicks = (e: MouseEvent): number => {
      return (e.target as any)._clicks;
    };
    const setClicks = (e: MouseEvent, value: number) => {
      (e.target as any)._clicks = value;
    }

    initClicks(e);
    setClicks(e, getClicks(e) + 1);
    if (getClicks(e) === 1) {
      setTimeout(() => {
        const id = data.Id;
        if (getClicks(e) === 1) {
          if (this.isSchedule(id)) {
            this.clickScheduleAction(e, data);
          } else {
            this.clickPreviewAction(e, data);
          }
        } else {
          if (this.isSchedule(id)) {
           this.altClickScheduleAction(e, data);
          } else {
            this.altClickPreviewAction(e, data);
          }
        }
        setClicks(e, 0);
      }, 200);
    }
    return false;
  }

  clickPreviewAction(e: MouseEvent, data: Record<string, any>) {
    if (e.metaKey === true || e.altKey === true || e.ctrlKey === true) {
      this.altClickPreviewAction(e, data);
    } else {
      const tableName = data.record.TableClass.value;
      const tableID = data.Id;
      const aspect = this.urlAspect;
      const view = "Popout" + tableName;
      this.navigateService.rightSlideInWindow(
        {
          'tile': 'DetailsAutoScaffold',
          'parameter-values': {
            table: tableName,
            id: tableID,
            aspect: aspect,
            view: view
          },
          'loadOptions': {
            cache: false,
            popup: true
          }
        },
        (eventData, envelope) => {
          });
    }
  }

  altClickPreviewAction(e: MouseEvent, data: Record<string, any>) {
    const tableName = data.record.TableClass.value;
    const tableID = data.Id;
    const aspect = this.urlAspect;

    this.navigateService.to({
      table: tableName,
      id: tableID,
      aspect: aspect
    });
  }

  clickScheduleAction(e: MouseEvent, data: Record<string, any>) {
    const tableName = data.Id;
    const aspect = this.urlAspect;

    this.navigateService.to({
      table: tableName,
      aspect: aspect
    });
  }

  altClickScheduleAction(e: MouseEvent, data: Record<string, any>) {
    this.clickScheduleAction(e, data);
  }

  resourceHeaderLink(data: ResourceDetails) {
    const id = data.resourceData.Id;

    const tableName = this.isSchedule(id) ? id : data.resourceData.record?.TableClass?.value;
    const tableID = this.isSchedule(id) ? "" : `/${id}` ;
    const aspect = this.urlAspect ? `/${this.urlAspect}` : "";
    return `/#/${tableName}${tableID}${aspect}`;
  }

  isScheduleEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    const id = data.resourceData?.Id || (data as any).Id;
    return id.endsWith("Schedule");
  }

  isBlockScheduleEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return this.isScheduleEvent(data) && (data as ScheduleEventModel).IsBlock;
  }

  isNonBlockScheduleEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return this.isScheduleEvent(data) && !(data as ScheduleEventModel).IsBlock;
  }

  isNonScheduleEvent(data: ScheduleEventModel | ResourceDetails | Record<string, any>) {
    return !this.isScheduleEvent(data);
  }

  private isSchedule(id: string) {
    return id.endsWith("Schedule");
  }

  // Quick info support

  getQuickInfoFormattedDateString(data: Record<string, any>): string {
    // getFormattedString is a private method in QuickPopups
    // @ts-ignore
    const argsData: { eventSubject: string, details: string } = this.schedule.quickPopup.getFormattedString(data);
    return argsData.details;
  }

  getQuickInfoFormattedSubject(data: Record<string, any> ) {
    return this.isScheduleEvent(data) ? data.Subject : `${data.record.Number.displayValue} - ${data.record.ShortDescription.displayValue}`;
  }

  getQuickInfoRecordValue(data: Record<string, any>, field: string): string {
    return data.record[field].displayValue;
  }

  // Event template support

  getTimeString(date: Date): string {
    return this.i18nService.formatDate(date, { skeleton: 'hm' });
  }

  getEventFormattedSubject(data: Record<string, any> ) {
    return this.isScheduleEvent(data) ? data.Subject : `${data.record.Number.displayValue} - ${data.record.ShortDescription.displayValue}`;
  }

  getEventFormattedTimeString(data: Record<string, any>) {
    return `${this.getTimeString(data.StartTime)} - ${this.getTimeString(data.EndTime)}`;
  }

  getEventFormattedStatus(data: Record<string, any>) {
    return data.record.WorkflowStatus.displayValue;
  }

  // Private data support

  private applyRecords() {
    const records = this.processRecords(this.scheduleModel);

    const model = this.scheduleModel.eventSettings;

    model.dataSource = records;

    this.eventSettings = cloneDeep(model);
  }

  private async applyConfiguration() {
    this.scheduleModel = await this.processConfiguration();

    this.mergeGrouping = this.scheduleModel.mergeGrouping.enabled;

    this.liveUpdate = this.scheduleModel.liveUpdate;

    this.group = this.scheduleModel.group;
    if (!this.grouping) {
      this.group.resources = [];
    }

    this.timezone = this.scheduleModel.timezone || this.userService.getTimeZone();
  }

  // Private methods for data

  private async loadRecords() {
    try {
      const tableToIds = await this.generateQueryFilterTableToIds();
      this.records = await this.scheduleDataService.loadRecords(this.scheduleConfiguration, this.queryFilterCurrentQuery, tableToIds);
    } catch (e) {
      console.error(e);
    }
  }

  private processRecords(scheduleModel: ScheduleModel): any[] {
    // Make the dates into JS Date objects
    this.records.forEach((r) => {
      if (r.StartTime) {
        r.StartTime = new Date(r.StartTime);
      }
      if (r.EndTime) {
        r.EndTime = new Date(r.EndTime);
      }
    });

    // Do merge grouping
    if (this.mergeGrouping && scheduleModel?.mergeGrouping?.enabled) {
      this.records.forEach((r)=> {
        scheduleModel.mergeGrouping.eventForeignIdFields.forEach((f) => {
          if (r[f]) {
            if (!r["_mergedField"]) {
              r["_mergedField"] = [];
            }
            r["_mergedField"].push(r[f]);
          }
        });
        Object.keys(scheduleModel.mergeGrouping.eventParentIds).forEach((k) => {
          const rec = scheduleModel.mergeGrouping.eventParentIds[k];
          if (r[k]) {
            r[rec.id] = rec.value;
          }
        });
      });
    }

    return this.records;
  }

  // Private methods for configuration

  private getConfig(): ScheduleConfigurationModel {
    return this.scheduleConfiguration;
  }

  private async loadConfiguration() {
    const config = await this.scheduleDataService.loadConfiguration(this.scheduleId);
    this.scheduleConfiguration = config.scheduleConfiguration;
    this.scheduleId = config.scheduleId;
    this.scheduleKey = config.scheduleKey;
  }

  private async processConfiguration() {
    const {title, subtitle, schedule} = await this.scheduleDataService.processConfiguration(this.getConfig());

    this.title = title;
    this.subtitle = subtitle;

    const tableToIds = await this.generateQueryFilterTableToIds();
    return await this.scheduleDataService.processScheduleModel(schedule, this.queryFilterCurrentQuery, tableToIds);
  }

  private async refreshResources() {
    const tableToIds = await this.generateQueryFilterTableToIds();
    const modelWithNewResources = await this.scheduleDataService.processScheduleModel(this.scheduleModel, this.queryFilterCurrentQuery, tableToIds);

    this.scheduleModel.resources = modelWithNewResources.resources;

    // Refresh the schedule in place
    this.schedule.resources.forEach((r) => {
      const name = r.name;
      const newResource = this.scheduleModel.resources.find((r) => r.name === name);
      const newDataSource = newResource?.dataSource || [];
      r.dataSource = newDataSource;
    });
  }

  // Filter support

  // Given the set of query filters construct a list of record ids that are applicable. The only case we have at the
  // moment is that QUERY_CMDB will generate a list of Change ids that are associated with a CI by having it either
  // be an impacted service, a potentially impacted CI, or a directly related CI.
  private async generateQueryFilterTableToIds() {
    const result: any = {};
    const cmdbQueryFilter = this.queryFilterCurrentQuery[this.QUERY_CMDB];
    if (cmdbQueryFilter && cmdbQueryFilter !== this.QUERY_EMPTY) {
      const ids = await this.getControllerData("getChangeIdsForCMDBQuery", {query: cmdbQueryFilter});
      result[this.QUERY_CHANGE] = ids;
    }
    return result;
  }

  // Value is json query
  queryOnApplyFilter(value: any, whichQuery: string) {
    this.queryFilterCurrentQuery[whichQuery] = JSON.stringify(value);
    this.queryFilterConfig[whichQuery].data.answer.value = this.queryFilterCurrentQuery[whichQuery];
    this.queryFilterConfig[whichQuery].data.answer.displayValue = this.queryFilterCurrentQuery[whichQuery];

    this.onRefreshData(null);
  }

  // Value is serialised query
  queryValueChangeCallback(value: string, whichQuery: string) {
    if (this.queryFilterIgnoreValueChange[whichQuery]) {
      console.debug('ForwardScheduleOfChangePage.queryValueChangeCallback - ignoring value change as not initialised yet', value);
    } else {
      this.queryFilterCurrentQuery[whichQuery] = value;
    }
  }

  // Exporting

  private onExportExcelClick(): void {
    this.schedule.exportToExcel();
  }

  private onExportICalClick(): void {
    this.schedule.exportToICalendar();
  }

  // Utilities

  private toggleScheduleComponent() {
    this.toggleSchedule = !this.toggleSchedule;
  }

  private async getControllerData(method: string, data: any = {}) {
    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Schedule")
      .method(method)
      .data(data)
      .execute();

    return response.data;
  }

  stringify(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }
}
